import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { PlanCode, QueryResult, SuperuserOrganisationDto } from '@flowqa/shared';

type SuperuserOrganisationQueryRequest = GridQueryInput & { includeDeleted?: boolean };

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const querySuperuserOrganisations = async (
  input: SuperuserOrganisationQueryRequest,
): Promise<QueryResult<SuperuserOrganisationDto>> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<QueryResult<SuperuserOrganisationDto>>(
      'superuser/organisations/query',
      {
        page: input.page,
        pageSize: input.pageSize,
        sorts: input.sorts,
        filters: input.filters,
        includeDeleted: input.includeDeleted,
      },
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

type SuperuserOrganisationUpdateInput = {
  id: string;
  name: string;
  planCode: PlanCode;
  trialEndsAt: string | null;
  userLimit: number;
  projectLimit: number;
  executionUploadLimit: number | null;
  deliveryEnabled: boolean;
  operationsEnabled: boolean;
  codeEnabled: boolean;
};

export const updateSuperuserOrganisation = async (
  input: SuperuserOrganisationUpdateInput,
): Promise<SuperuserOrganisationDto> => {
  const authToken = ensureToken();
  try {
    return await apiClient.patch<SuperuserOrganisationDto>(
      `superuser/organisations/${input.id}`,
      {
        name: input.name,
        planCode: input.planCode,
        trialEndsAt: input.trialEndsAt,
        userLimit: input.userLimit,
        projectLimit: input.projectLimit,
        executionUploadLimit: input.executionUploadLimit,
        deliveryEnabled: input.deliveryEnabled,
        operationsEnabled: input.operationsEnabled,
        codeEnabled: input.codeEnabled,
      },
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const wipeSuperuserOrganisationData = async (id: string): Promise<void> => {
  const authToken = ensureToken();
  try {
    await apiClient.post<{ status: 'wiped' }>(
      `superuser/organisations/${id}/wipe-data`,
      {},
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteSuperuserOrganisation = async (id: string): Promise<void> => {
  const authToken = ensureToken();
  try {
    await apiClient.post<{ status: 'deleted' }>(
      `superuser/organisations/${id}/delete`,
      {},
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const retrySuperuserOrganisationExternalCleanup = async (
  id: string,
): Promise<{ status: 'retried' }> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<{ status: 'retried' }>(
      `superuser/organisations/${id}/retry-external-cleanup`,
      {},
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};
